/**
 * Loop preset bundle helpers for cart subscribe toggles.
 * Updates _loopBundleDiscountAttributes when selling plan changes so checkout
 * uses SUBSCRIPTION vs ONETIME discount (e.g. $60.345 vs $67.05 on 3-packs).
 */
(function () {
  const CDN = 'https://cdn.loopwork.co';

  function getShopDomain() {
    return window.Shopify?.shop || document.documentElement.dataset.shopifyDomain || '';
  }

  async function fetchStoreConfig(domain) {
    const cacheKey = `loop_store_${domain}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        /* ignore */
      }
    }

    const res = await fetch(`${CDN}/${domain}/store.json`);
    if (!res.ok) return null;
    const data = await res.json();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  }

  async function fetchBundleConfig(domain, productId) {
    const cacheKey = `loop_bundle_${domain}_${productId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        /* ignore */
      }
    }

    const res = await fetch(`${CDN}/${domain}/presetBundles/${productId}.json`);
    if (!res.ok) return null;
    const data = await res.json();
    sessionStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  }

  async function createBundleTransaction(storeConfig, payload) {
    const authToken = storeConfig?.sentinalAuthToken;
    if (!authToken) throw new Error('Missing Loop auth token');

    const res = await fetch('https://sentinel.loopwork.co/bundles/createPresetBundleTransaction', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: authToken,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      console.error('[LoopBundleCart] Transaction error:', errData);
      throw new Error('Loop bundle transaction failed');
    }

    const data = await res.json();
    return data?.data?.transactionId;
  }

  function computeDiscountValue(discount, sellingPlanId, packQty, addParentForced) {
    const multiplier = addParentForced ? 1 : packQty || 1;
    if (sellingPlanId && discount.sellingPlanComputedDiscounts?.[String(sellingPlanId)] != null) {
      return Number(discount.sellingPlanComputedDiscounts[String(sellingPlanId)]) * multiplier;
    }
    return Number(discount.oneTimeDiscount || 0) * multiplier;
  }

  function parseBundleDiscountAttributes(cart) {
    try {
      const raw = cart?.attributes?._loopBundleDiscountAttributes;
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  function getBundleTxnIdFromItem(item) {
    const props = item?.properties || {};
    return props._bundleId || props._loopBundleTxnId || '';
  }

  function isLoopBundleItem(item) {
    const props = item?.properties || {};
    if (props._isPresetBundleProduct === true || props._isPresetBundleProduct === 'true') return true;
    return !!(props._bundleId || props._loopBundleTxnId);
  }

  function getLoopMetaFromItem(item) {
    const props = item?.properties || {};
    return {
      presetProductId: props._loopPresetProductId,
      presetVariantId: props._loopPresetVariantId,
      bundleTxnId: getBundleTxnIdFromItem(item),
      packQty: Number(props._loopPackQty) || 1,
      addParentForced: props._loopAddParentForced === 'true',
    };
  }

  /**
   * Refresh Loop bundle transaction + cart attributes when subscribe toggles.
   * Updates all cart lines that share the same bundle transaction id.
   */
  async function syncLoopBundleSellingPlan({
    shopDomain,
    cart,
    triggerLineIndex,
    sellingPlanId,
    sectionIds,
  }) {
    const domain = shopDomain || getShopDomain();
    if (!domain || !cart?.items?.length) return null;

    const lineIdx = Number(triggerLineIndex) - 1;
    const triggerItem = cart.items[lineIdx];
    if (!triggerItem || !isLoopBundleItem(triggerItem)) return null;

    const meta = getLoopMetaFromItem(triggerItem);
    if (!meta.presetProductId || !meta.presetVariantId || !meta.bundleTxnId) {
      console.warn('[LoopBundleCart] Missing Loop meta on line item — re-add from product page');
      return null;
    }

    const storeConfig = await fetchStoreConfig(domain);
    if (!storeConfig) throw new Error('Could not load Loop store config');

    const bundleConfig = await fetchBundleConfig(domain, meta.presetProductId);
    if (!bundleConfig) throw new Error('Could not load Loop bundle config');

    const bundleVariant = bundleConfig.variants?.find(
      (v) => String(v.shopifyId) === String(meta.presetVariantId),
    );
    if (!bundleVariant) throw new Error('Preset variant not found in Loop bundle config');

    const isSubscription = !!sellingPlanId;
    const discount = bundleVariant.mappedDiscounts?.find((d) =>
      isSubscription ? d.purchaseType === 'SUBSCRIPTION' : d.purchaseType !== 'SUBSCRIPTION',
    );
    if (!discount) throw new Error('No Loop discount for this purchase type');

    const transactionPayload = {
      presetProductShopifyId: Number(bundleConfig.id),
      presetDiscountId: discount.id,
      presetVariantShopifyId: Number(meta.presetVariantId),
      totalQuantity: meta.packQty,
    };
    if (sellingPlanId) {
      transactionPayload.sellingPlanShopifyId = Number(sellingPlanId);
    }

    const newTxnId = await createBundleTransaction(storeConfig, transactionPayload);
    if (!newTxnId) throw new Error('Loop did not return a transaction id');

    const oldTxnId = String(meta.bundleTxnId);
    const existingAttrs = parseBundleDiscountAttributes(cart);
    const activeTxnIds = new Set(
      cart.items.map((item) => String(getBundleTxnIdFromItem(item))).filter(Boolean),
    );
    activeTxnIds.delete(oldTxnId);
    activeTxnIds.add(String(newTxnId));

    const cleanedAttrs = Object.keys(existingAttrs)
      .filter((key) => activeTxnIds.has(String(key)) && String(key) !== oldTxnId)
      .reduce((obj, key) => {
        obj[key] = existingAttrs[key];
        return obj;
      }, {});

    const discountValue = computeDiscountValue(
      discount,
      sellingPlanId,
      meta.packQty,
      meta.addParentForced,
    );

    cleanedAttrs[newTxnId] = {
      discountType: discount.type,
      discountValue: discount.value,
      discountComputedValue: discountValue,
    };

    const updateBody = {
      attributes: {
        _loopBundleDiscountAttributes: JSON.stringify(cleanedAttrs),
      },
    };
    if (sectionIds?.length) {
      updateBody.sections = sectionIds.join(',');
      updateBody.sections_url = window.location.pathname;
    }

    let latestCart = cart;
    const updateRes = await fetch(window.Theme?.routes?.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(updateBody),
    });
    latestCart = await updateRes.json();
    if (latestCart.status || latestCart.errors) {
      throw new Error(latestCart.description || latestCart.message || 'Could not update Loop cart attributes');
    }

    const bundleLineIndexes = [];
    latestCart.items?.forEach((item, index) => {
      if (String(getBundleTxnIdFromItem(item)) === oldTxnId) {
        bundleLineIndexes.push(index + 1);
      }
    });

    for (const bundleLine of bundleLineIndexes) {
      const item = latestCart.items[bundleLine - 1];
      const props = { ...(item.properties || {}) };
      props._bundleId = String(newTxnId);
      props._loopBundleTxnId = String(newTxnId);

      const changeBody = {
        line: bundleLine,
        quantity: item.quantity,
        properties: props,
      };
      if (sellingPlanId) {
        changeBody.selling_plan = Number(sellingPlanId);
      } else {
        changeBody.selling_plan = '';
      }
      if (sectionIds?.length) {
        changeBody.sections = sectionIds.join(',');
        changeBody.sections_url = window.location.pathname;
      }

      const changeRes = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(changeBody),
      });
      latestCart = await changeRes.json();
      if (latestCart.status || latestCart.errors) {
        throw new Error(latestCart.description || latestCart.message || 'Could not update bundle line');
      }
    }

    return latestCart;
  }

  window.LoopBundleCart = {
    getShopDomain,
    isLoopBundleItem,
    getLoopMetaFromItem,
    syncLoopBundleSellingPlan,
  };
})();
